import React, { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { updateAccessToken, updateRefreshToken, updateUserToken } from 'services/auth';
import { appIsWorkingVar, isLoggedInVar } from 'graphql/cache';
import { useNavigate } from 'react-router-dom';
import AuthLayout from '../components/AuthLayout';
import { getLoginValidationSchema } from '../helpers';
import { LoginModelProp, MfaRequestProps } from '../types';
import { useAuth, UseAuthPropTypes } from '../hooks/useAuthentication';
import { NavigationPath } from '../../../types/DomainTypes';
import { DisplayField, DisplayFieldType } from '../../../components/DisplayForms';
import { Button } from '../../../components';
import { classNames, validateEmail } from '../../../utils';
import { getFieldAttributes } from '../../../helpers';
import DisplayForm from '../../../components/DisplayForms/DisplayForm';
import { logError } from '../../../components/LogError';
import { ReactComponent as AppStoreLogo } from '../../../assets/icons/appStore.svg';
import { ReactComponent as PlayStoreLogo } from '../../../assets/icons/playStore.svg';

type Model = {
  [key in LoginModelProp]: string;
};
type FormValidationType = {
  [Key in LoginModelProp]: boolean;
};
const initialModel = {
  [`${LoginModelProp.EMAIL}`]: '',
  [`${LoginModelProp.PASSWORD}`]: '',
} as Model;

const validateLoginFormFunc = (value: string, modelKey: string) => {
  if (modelKey === LoginModelProp.EMAIL) {
    return validateEmail(value);
  }
  if (modelKey === LoginModelProp.PASSWORD) {
    return value.length >= 8;
  }
  return false;
};

interface LoginFormProps {
  emailAddress?: string;
  onMFARequested?: UseAuthPropTypes['onMFARequested'];
  onSucess?: () => void;
}

export const LoginForm: FC<LoginFormProps> = ({ onMFARequested, emailAddress, onSucess }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [isForceValid, setIsForceValid] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [formValidation, setFormValidation] = useState<FormValidationType>({
    [LoginModelProp.PASSWORD]: false,
    [LoginModelProp.EMAIL]: false,
  });

  const [model, setModel] = useState<Model>({
    ...initialModel,
  });

  const errorHandler = (message: string) => {
    throw new Error(message);
  };

  const { onLogin } = useAuth({
    errorHandler,
    onError: (e: Error) => {
      setIsLoggingIn(false);
      setError(e);
      logError(e);
    },
    onMFARequested: (request: MfaRequestProps) => {
      setIsLoggingIn(false);
      if (onMFARequested) onMFARequested(request);
    },
    onSuccess: ({ portalAuthLogin: { accessToken, refreshToken, userToken } }) => {
      setIsLoggingIn(false);
      updateAccessToken(accessToken);
      updateRefreshToken(refreshToken);
      updateUserToken(userToken);
      onSucess?.();
      navigate(NavigationPath.PORTFOLIO);
      isLoggedInVar(true);
      appIsWorkingVar(false);
    },
  });
  const onClear = (key: string) => {
    if (isForceValid) setIsForceValid(false);
    formik.setFieldValue(key, '');
    setFormValidation({ ...formValidation, [key]: false });
  };

  const updateFormValidation = (value: string, modelKey: LoginModelProp) => {
    const validationResult = validateLoginFormFunc(value, modelKey);
    if (formValidation[modelKey] !== validationResult) {
      setFormValidation({ ...formValidation, [modelKey]: validationResult });
    }
  };
  const onForgetPassword = () => {
    navigate(NavigationPath.FORGET_PASSWORD);
  };

  const onRegister = () => {
    navigate(NavigationPath.REGISTRATION);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, field?: DisplayField) => {
    e.preventDefault();
    formik.handleChange(e);
    if (isForceValid) setIsForceValid(false);
    const { value } = e.target;
    updateFormValidation(value, field!.modelKey as LoginModelProp);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>, field?: DisplayField) => {
    formik.handleBlur(e);
    if (isForceValid) setIsForceValid(false);
    const { value } = e.target;
    updateFormValidation(value, field!.modelKey as LoginModelProp);
  };

  const mValidationSchema = yup.object({
    ...getLoginValidationSchema(t),
  });

  const formik = useFormik<Model>({
    initialValues: { ...initialModel, email: emailAddress || '' },
    onSubmit: async (values) => {
      setIsLoggingIn(true);
      onLogin(values);
    },
    validationSchema: mValidationSchema || model,
  });
  const onFieldUpdate = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, field?: DisplayField) => {
    handleChange(e, field);
    setModel({ ...model, [`${field!.modelKey}`]: e.target.value });
  };

  const loginForm = useMemo(() => {
    const emailHasError = formik.touched[LoginModelProp.EMAIL] && formik.errors[LoginModelProp.EMAIL];
    const emailAttrs = getFieldAttributes(LoginModelProp.EMAIL, LoginModelProp.EMAIL, t('auth:loginForm.email.title'));
    const emailField = {
      ...emailAttrs,
      disabled: isLoggingIn,
      inputProps: {
        showClearButton: true,
        inputClassName:
          'bg-transparent dark:bg-transparent text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500',
        inputContainerClassName: classNames(
          'rounded-xl border-2 transition-all duration-200',
          emailHasError
            ? '!border-red-400 dark:!border-red-500'
            : '!border-gray-200 dark:!border-slate-600 focus-within:!border-orange dark:focus-within:!border-teal-500',
        ),
      },
      onClear: (dField?: DisplayField) => {
        if (dField) onClear(dField.id);
      },
      onChange: onFieldUpdate,
      onBlur: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>, field?: DisplayField) => {
        handleBlur(e, field);
      },

      helperText: emailHasError ? formik.errors[LoginModelProp.EMAIL] : '',
      label: {
        ...emailAttrs.label,
        className: classNames(
          'text-sm font-medium mb-1.5',
          emailHasError ? 'text-red-500 dark:text-red-400' : 'text-gray-700 dark:text-gray-300',
        ),
      },
    };
    const pwdAttrs = getFieldAttributes(
      LoginModelProp.PASSWORD,
      LoginModelProp.PASSWORD,
      t('auth:loginForm.password.title'),
    );
    const pwdHasError = formik.touched[LoginModelProp.PASSWORD] && formik.errors[LoginModelProp.PASSWORD];
    const passwordField = {
      ...pwdAttrs,
      disabled: isLoggingIn,
      className: '',
      type: DisplayFieldType.PASSWORD,
      inputProps: {
        showClearButton: true,
        inputClassName:
          'bg-transparent dark:bg-transparent text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500',
        inputContainerClassName: classNames(
          'rounded-xl border-2 transition-all duration-200',
          pwdHasError
            ? '!border-red-400 dark:!border-red-500'
            : '!border-gray-200 dark:!border-slate-600 focus-within:!border-orange dark:focus-within:!border-teal-500',
        ),
        onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
          if (e.key.toLowerCase() === 'enter') {
            formik.handleSubmit();
          }
        },
      },
      helperText: pwdHasError ? formik.errors[LoginModelProp.PASSWORD] : '',
      onChange: onFieldUpdate,
      onBlur: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>, fieldIn?: DisplayField) => {
        handleBlur(e, fieldIn);
      },
      onClear: (dField?: DisplayField) => {
        if (dField) onClear(dField.id);
      },
      label: {
        ...pwdAttrs.label,
        className: classNames(
          'text-sm font-medium mb-1.5',
          pwdHasError ? 'text-red-500 dark:text-red-400' : 'text-gray-700 dark:text-gray-300',
        ),
      },
    };

    const isFormValid =
      isForceValid || (formValidation[LoginModelProp.PASSWORD] && formValidation[LoginModelProp.EMAIL]);
    return {
      isValid: isFormValid,
      sections: [
        {
          className: 'w-full gap-5',
          fields: [emailField, passwordField],
        },
      ],
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model, formik]);

  const formIsValid = isForceValid || (formValidation[LoginModelProp.PASSWORD] && formValidation[LoginModelProp.EMAIL]);

  useEffect(() => {
    const validations = Object.keys(initialModel).reduce((result: boolean[], key) => {
      const value = formik.values[key as keyof Model];
      updateFormValidation(value, key as LoginModelProp);
      return [...result, validateLoginFormFunc(value, key)];
    }, []);
    if (!validations.includes(false)) setIsForceValid(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthLayout
      title={t('auth:loginForm.title')}
      error={{ body: error?.message || '', onClose: () => setError(null) }}
      subTitle={t('auth:loginForm.subTitle')}
    >
      <div className="w-full max-w-[420px] px-4 sm:px-0">
        {/* Form Card */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl dark:shadow-slate-900/50 p-6 sm:p-8 mt-6 sm:mt-8 border border-gray-100 dark:border-slate-700/50">
          <DisplayForm
            titleContainerClassName="mt-0"
            sectionsContainerClassName="pb-1"
            sections={loginForm.sections}
            title=""
            model={{ ...model, modelType: 'login' }}
          />

          {/* Forgot Password Link */}
          <div className="mt-4 flex justify-end">
            <Button
              isLink={true}
              onClick={onForgetPassword}
              className="text-sm text-orange dark:text-teal-400 hover:text-orange/80 dark:hover:text-teal-300 font-medium transition-colors"
            >
              {t('auth:loginForm.forgotPasswordLink')}
            </Button>
          </div>

          {/* Login Button */}
          <Button
            onClick={formik.handleSubmit}
            type="submit"
            isDisable={!formIsValid}
            className={`w-full mt-6 h-12 text-base font-semibold rounded-xl transition-all duration-200 ${
              !formIsValid
                ? 'bg-gray-200 dark:bg-slate-700 text-gray-400 dark:text-slate-500 cursor-not-allowed'
                : 'bg-gradient-to-r from-orange to-orange/90 dark:from-teal-500 dark:to-teal-600 text-white shadow-lg shadow-orange/30 dark:shadow-teal-500/30 hover:shadow-xl hover:shadow-orange/40 dark:hover:shadow-teal-500/40 hover:scale-[1.02] active:scale-[0.98]'
            }`}
            isProcessing={isLoggingIn}
            props={{
              name: 'login',
            }}
          >
            {t('auth:loginForm.login')}
          </Button>
        </div>

        {/* Register Link */}
        <div className="w-full text-sm mt-6 flex justify-center">
          <span className="text-gray-600 dark:text-gray-400">
            {t('auth:loginForm.registerInfo')}
            <button
              className="text-orange dark:text-teal-400 hover:text-orange/80 dark:hover:text-teal-300 font-medium ml-1.5 transition-colors"
              onClick={onRegister}
            >
              {t('auth:loginForm.registerLink')}
            </button>
          </span>
        </div>

        {/* Divider */}
        <div className="flex items-center my-8">
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-300 dark:via-slate-600 to-transparent" />
        </div>

        {/* App Download Section */}
        <div className="flex-col items-center flex">
          <span className="text-sm text-gray-500 dark:text-gray-400 text-center mb-4">{t('auth:downloadApp')}</span>
          <div className="flex flex-row gap-3">
            <a
              href="https://apps.apple.com/app/cult-wines-investment-portal/id1660208790"
              target="_blank"
              rel="noreferrer"
              className="transform hover:scale-105 transition-transform duration-200"
            >
              <AppStoreLogo className="h-10 w-auto" />
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=com.portalmobile.cw"
              target="_blank"
              rel="noreferrer"
              className="transform hover:scale-105 transition-transform duration-200"
            >
              <PlayStoreLogo className="h-10 w-auto" />
            </a>
          </div>
        </div>
      </div>
    </AuthLayout>
  );
};
